import React, { useEffect, useCallback, useState } from "react"
import { useParams } from "react-router-dom"
import styles from "./index.module.css"
import { Card, Flex, Typography, Button, Form } from "antd"
import { useTranslation } from "react-i18next"
import AddressIcon from "../../../../assets/icons/Address.svg"
import CalendarIcon from "../../../../assets/icons/calendar.svg"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { useSharedWaiverData } from "./hooks/useSharedWaiverData"
import { useParentForm } from "./hooks/useParentForm"
import { WaiverOptions } from "./components/WaiverOptions"
import { WaiverDetails } from "./components/WaiverDetails"
import { ParentGuardianForm } from "./components/ParentGuardianForm"
import { MinorGuestForm } from "./components/MinorGuestForm"

const { Title } = Typography

export default function SharedWaivers() {
  const { t } = useTranslation()
  const { id } = useParams()

  const { selected, setSelected, parentData, setParentData, form } = useParentForm()
  const guardianForm = Form.useForm()[0]
  // Reset forms when switching between adult and guardian
  React.useEffect(() => {
    if (selected === "adult" && form) {
      form.resetFields()
      setShowAdultForm(false)
    } else if (selected === "guardian" && guardianForm) {
      guardianForm.resetFields()
      setShowGuardianForm(false)
    }
    setIsFormValid(false)
  }, [selected])
  const [isFormValid, setIsFormValid] = useState(false)
  const [showAdultForm, setShowAdultForm] = useState(false)
  const [showGuardianForm, setShowGuardianForm] = useState(false)

  // Check if adult form is valid
  const checkAdultFormValidity = useCallback(() => {
    if (selected !== "adult" || !form) return false

    const values = form.getFieldsValue()
    const requiredFields = ["firstName", "lastName", "email", "dob", "phone"]

    return requiredFields.every((field) => {
      const value = values[field]
      if (field === "phone") {
        return value?.fullNumber?.trim()
      }
      return value?.toString().trim()
    })
  }, [selected, form])

  // Check if guardian form is valid
  const checkGuardianFormValidity = useCallback(() => {
    if (selected !== "guardian" || !guardianForm) return false

    const values = guardianForm.getFieldsValue()
    const minors = values.minors || []

    if (minors.length === 0) return false

    return minors.every((minor) => {
      return minor.fullName?.trim() && minor.dateOfBirth
    })
  }, [selected, guardianForm])

  // Update form validity when selection or form values change
  useEffect(() => {
    let valid = false
    if (selected === "adult") {
      // Enable next button immediately when adult is selected (before showing form)
      valid = !showAdultForm
    } else if (selected === "guardian") {
      // Enable next button immediately when guardian is selected (before showing form)
      valid = !showGuardianForm
    }
    setIsFormValid(valid)
  }, [selected, showAdultForm, showGuardianForm])

  // Update form validity when adult form values change
  useEffect(() => {
    if (selected === "adult" && showAdultForm) {
      const valid = checkAdultFormValidity()
      setIsFormValid(valid)
    }
  }, [selected, showAdultForm, checkAdultFormValidity])

  // Update form validity when guardian form values change
  useEffect(() => {
    if (selected === "guardian" && showGuardianForm) {
      const valid = checkGuardianFormValidity()
      setIsFormValid(valid)
    }
  }, [selected, showGuardianForm, checkGuardianFormValidity])

  const handleNext = useCallback(() => {
    if (selected === "adult" && !showAdultForm) {
      setShowAdultForm(true)
      setIsFormValid(false)
    } else if (selected === "guardian" && !showGuardianForm) {
      setShowGuardianForm(true)
      setIsFormValid(false)
    } else {
      console.log("Next clicked", { selected, parentData })
    }
  }, [selected, parentData, showAdultForm, showGuardianForm])

  return (
    <Flex className={styles.container} align="center" justify="center">
      <Card className={styles.card}>
        <Title level={3} className={styles.title}>
          {t("digital_waiver_and_contracts.waiver_form")}
        </Title>

        <WaiverDetails
          title="Catching Redfish in Panama city"
          date="Sept 15, 2024"
          location="Panama City Beach"
          calendarIcon={CalendarIcon}
          addressIcon={AddressIcon}
        />

        <div className={`mb-4xl ${styles.title}`}>{t("embedded.booking_confirmation.who_is_participation")}</div>

        <WaiverOptions selected={selected} onSelect={setSelected} className={`mb-4xl ${styles.optionsRow}`} />

        {selected === "adult" && showAdultForm && (
          <ParentGuardianForm
            form={form}
            parentData={parentData}
            setParentData={setParentData}
            t={t}
            validatePhoneNumber={validatePhoneNumber}
            setIsFormValid={setIsFormValid}
          />
        )}
        {selected === "guardian" && showGuardianForm && <MinorGuestForm t={t} form={guardianForm} setIsFormValid={setIsFormValid} />}

        <Flex justify="center" className={styles.buttonContainer}>
          <Button color="primary" variant="solid" className={styles.nextButton} onClick={handleNext} disabled={!isFormValid}>
            {t("embedded.booking_confirmation.next")}
          </Button>
        </Flex>
      </Card>
    </Flex>
  )
}
